import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import useTranslation from 'next-translate/useTranslation';
import Modal from 'react-bootstrap/Modal';
import get from 'lodash/get';
import has from 'lodash/has';
import camelCase from 'lodash/camelCase';
import moment from 'moment';
import cx from 'clsx';
import { InView } from 'react-intersection-observer';
import { connect } from 'react-redux';
import sidekick from '@last-rev/contentful-sidekick-util';
import styles from './PageCourseTopic.module.scss';
import PageCourseTopicPropTypes from './PageCourseTopicPropTypes';
import CourseFinishedPopup from '../CourseFinishedPopup';
import RichTextParser from '../RichTextParser';
import SocialLinks from '../SocialLinks';
import Link from 'next/link';
import { useRouter } from 'next/dist/client/router';
import {
  completeCourse,
  completeTopicForCourse,
  setViewedCompletiondMessageForCourse
} from '../../redux/modules/courses';
import getThemeClass from '../../utils/getThemeClass';

const mapStateToProps = ({ user, courses }) => ({
  loggedIn: !!user.id,
  userLoaded: user.loaded,
  completedCourses: courses.completed,
  completedCoursesLoaded: courses.loaded
});

const mapDispatchToProps = {
  markTopicAsCompleted: completeTopicForCourse,
  markCourseAsCompleted: completeCourse,
  markCompletionMessageShown: setViewedCompletiondMessageForCourse
};

const collectLinks = (node, entriesLinks = []) => {
  const {
    data: { target },
    content
  } = node;

  if (content?.length) {
    content.forEach((item) => collectLinks(item, entriesLinks));
  }

  if (target?._contentTypeId === 'elementLink' && target?.destinationType === 'Set Anchor Tag') {
    entriesLinks.push({ ...target, href: `#${target?.linkText?.toLowerCase()?.replace(/ /g, '-')}` });
  } else if (target?._contentTypeId === 'layoutSection') {
    target.columns.forEach((column) => {
      if (column?._contentTypeId === 'elementLink' && column?.destinationType === 'Set Anchor Tag') {
        entriesLinks.push({ ...column, href: `#${column?.linkText?.toLowerCase()?.replace(/ /g, '-')}` });
      }
    });
  }
  return entriesLinks;
};

function parseRichTextField(richText) {
  if (!richText) return [];
  return collectLinks(richText);
}

function PageCourseTopic({
  _id,
  /* _href,
  _as */
  _contentTypeId,
  eyebrow,
  name,
  description,
  publishDate,
  readingTime,
  content /* seo */,
  course,
  // redux state
  loggedIn,
  userLoaded,
  completedCourses,
  completedCoursesLoaded,
  // redux actions
  markTopicAsCompleted,
  markCompletedOnScrollToBottom,
  markCompletionMessageShown,
  showToC
}) {
  const [enableCompletionPopup, setEnableCompletionPopup] = useState(false);
  const [url, setUrl] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [currentSection, setCurrentSection] = useState(null);
  const [showSideNav, setShowSideNav] = useState(true);
  const [showpopUpModal, setShowPopUpModal] = useState(true);
  const modalShown = useRef(false);
  const sectionNavRef = useRef(null);
  const delayObserver = useRef(false);
  const observesideNav = useRef()
  const { t } = useTranslation();
  const router = useRouter()
  const triggerCompletionMessage = useCallback(() => {
    setEnableCompletionPopup(true);
    markCompletionMessageShown(get(course, '_id'));
  }, [course, markCompletionMessageShown]);

  useEffect(() => {
    if (course && completedCoursesLoaded) {
      if (has(completedCourses, course._id)) {
        if (!get(completedCourses, `['${course._id}'].viewcompletionmessage`, false)) {
          triggerCompletionMessage();
        }
      }
    }
  }, [completedCoursesLoaded, completedCourses, triggerCompletionMessage, course]);

  // useEffect(() => {
  //   const el = observesideNav.current;
  //   if (!el) return;

  //   const reCalHeight = () => {
  //     // reset first
  //     el.style.height = "100%";
  //     el.style.maxWidth = "280px";

  //     const gb = el.getBoundingClientRect();

  //     const visibleTop = Math.max(gb.top, 0);
  //     const visibleBottom = Math.min(gb.bottom, window.innerHeight);
  //     const visibleHeight = Math.max(0, visibleBottom - visibleTop);

  //     if (Math.round(visibleHeight) < Math.round(gb.height)) {
  //       el.classList.add(styles.scrollbar);
  //       el.style.height = `${visibleHeight}px`;
  //       el.style.maxWidth = "300px";
  //     } else {
  //       el.classList.remove(styles.scrollbar);
  //     }
  //   };

  //   const runAfterPaint = () => {
  //     requestAnimationFrame(() => {
  //       requestAnimationFrame(reCalHeight);
  //     });
  //   };

  //   runAfterPaint();

  //   const observer = new ResizeObserver(runAfterPaint);
  //   observer.observe(el);

  //   window.addEventListener("resize", runAfterPaint);

  //   return () => {
  //     observer.disconnect();
  //     window.removeEventListener("resize", runAfterPaint);
  //   };
  // }, [router.asPath]);

  const bottomViewChange = useCallback(
    (inView, _entry) => {
      if (!inView) {
        setShowModal(false);
      } else if (loggedIn) {

        markTopicAsCompleted(_id, get(course, '_id'), get(course, 'topics'));
      } else if (!modalShown.current) {

        setShowModal(true);
        modalShown.current = true;
      }
    },
    [_id, course, loggedIn, markTopicAsCompleted]
  );

  const themeClassName = () =>
    course ? `theme-${getThemeClass({ _contentTypeId: course._contentTypeId, category: course.category })}` : null;

  const anchorTags = useMemo(() => parseRichTextField(content), [content]);
  useEffect(() => {
    const sectionObserver = new IntersectionObserver(
      (entries) => {
        if (delayObserver.current) return;
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setCurrentSection(entry.target.id);
          }
        });
      },
      { threshold: 0 }
    );
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            sectionNavRef?.current?.classList.add(styles.footerInView);
          } else {
            sectionNavRef?.current?.classList.remove(styles.footerInView);
          }
        });
      },
      { threshold: 0 }
    );


    const sections = document.querySelectorAll('span[id]');
    const footer = document.querySelector('footer');

    sections.forEach((section) => {
      sectionObserver.observe(section);
    });


    observer.observe(footer);

    return () => {
      sections.forEach((section) => {
        sectionObserver.unobserve(section);
      });

      observer.unobserve(footer);
    };
  }, [router.asPath]);
  useEffect(() => {
    setUrl(window.location.href);
  }, [url]);
  const hanldeSignInClick = (value) => {
    if (typeof window === "undefined") return;
    if (value) {
      localStorage.setItem("userSignedIN", "true");
    } else {
      localStorage.removeItem("userSignedIN");
    }
  }
  return (
    <>
      <div
        data-testid="PageCourseTopic"
        className={styles.pageCourseTopic}
        {...sidekick(_id, null, null, 'Page - Course Topic')}
        itemScope
        itemType="http://schema.org/Article">
        <div className={styles.layout}>

          <article className={!(showToC && anchorTags) ? styles.noToc:""}>
            {eyebrow ? (
              <p className={styles.eyebrow} data-testid="PageCourseTopic-eyebrow" {...sidekick(_id, 'eyebrow')}>
                {eyebrow}
              </p>
            ) : null}

            {name ? (
              <h1 className={styles.name} data-testid="PageCourseTopic-name" {...sidekick(_id, 'name')} itemProp="name">
                {name}
              </h1>
            ) : null}

            {description && description?.trim() !== '' ? (
              <h2
                className={styles.description}
                data-testid="PageCourseTopic-description"
                {...sidekick(_id, 'description')}>
                {description}
              </h2>
            ) : null}

            <div className="d-flex justify-content-between flex-wrap">
              {publishDate || readingTime ? (
                <div>

                  {readingTime ? (
                    <p
                      className={cx(styles.readingTime, publishDate && styles.hasDate)}
                      data-testid="PageCourseTopic-readingTime"
                      {...sidekick(_id, 'readingTime')}
                      itemProp="timeRequired">
                      {readingTime} {t('common:readingTime')}
                    </p>
                  ) : null}
                </div>
              ) : null}

              {url ? <SocialLinks url={url} /> : null}
            </div>

            {content ? (
              <section className={cx(styles.contentSection, themeClassName())} data-testid="PageCourseTopic-content">
                <RichTextParser document={content} _contentTypeId={_contentTypeId} />

              </section>
            ) : null}
          </article>
          {showSideNav ? (
            <div className={cx(styles.sideNav, !(showToC && anchorTags) && styles.noToc)}>
              {showToC && anchorTags ? (
                <nav className={styles.sectionNav} ref={sectionNavRef}>
                  <ul ref={observesideNav}>
                    {anchorTags.map((anchor) => {
                      return (
                        <li
                          key={camelCase(anchor._id)}
                          className={anchor.href === `#${currentSection}` ? styles.active : styles.deactive}>
                          <a href={anchor.href} onClick={(e) => {
                            e.preventDefault();

                            const sectionId = anchor.href.replace('#', '');
                            delayObserver.current = true;

                            setCurrentSection(sectionId);

                            document.getElementById(sectionId)?.scrollIntoView({
                              behavior: 'smooth',
                            });


                            setTimeout(() => {
                              delayObserver.current = false;
                            }, 800);
                          }} data-uxa-log={`course_module_topic_${anchor.linkText.toLowerCase().replace(/\s+/g, '_')}`} data-uxa-interactions="click shown">{anchor.linkText}</a>
                        </li>)
                    })}
                  </ul>
                </nav>
              ) : null}
            </div>
          ) : <div className={cx(styles.sideNav, !(showToC && anchorTags) && styles.noToc)} ></div>}
        </div>
      </div>

      {course ? (
        <>
          <InView skip={!userLoaded} onChange={(inView, entry) => bottomViewChange(inView, entry)} />
          {enableCompletionPopup && (
            <Modal
              show={showpopUpModal}
              onHide={() => setShowPopUpModal(false)}
              backdrop="static"
              scrollable={false}
              centered
              className={`${styles.modalDialog}`}
              contentClassName={styles.modalContent}>
              <Modal.Header closeButton className={styles.courseCompletedPromptModalHeader} />
              <Modal.Body className={styles.courseCompletedPromptModalBody}>
                <div className={styles.courseCompletedPromptWrap}>
                  <div className={styles.completionIcon}></div>
                  <div className={styles.courseCompletedPromptHeaderText}>{t('common:formInputLabelCourseCompleted')}</div>
                  <div className={styles.courseCompletedPromptBodyText}>{t('common:popUpBodyText')}</div>
                  <div className={styles.courseCompletedPromptCtaWrap}>
                    <Link className={styles.courseCompletedPromptCta} href="/self-guided-learning">
                      {t('common:viewAllCourses')}
                    </Link>
                  </div>
                </div>
              </Modal.Body>
            </Modal>
          )}
        </>
      ) : null}
      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        backdrop="static"
        scrollable
        centered
        size="lg"
        className={styles.modalDialog}
        contentClassName={styles.modalContent}>
        <Modal.Header closeButton className={styles.signInPromptModalHeader} onClick={() => { hanldeSignInClick()}}/>
        <Modal.Body className={styles.signInPromptModalBody}>
          <div className={styles.signInPromptWrap}>
            <div className={styles.signInICon}></div>
            <div className={styles.signInPromptHeaderText}>{t('common:textSigninModalIntro')}</div>
            <div className={styles.signInPromptBodyText}>{t('common:signInpopUpBodyText')}.</div>

            <div className={styles.signInPromptCtaWrap}>
              <a className={styles.signInPromptCta} href={`https://www.dropbox.com/login?cont=${url}`} onClick={() => { hanldeSignInClick("signin")}}>
                {t('common:ctaTextSignIn')}
              </a>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
}

PageCourseTopic.propTypes = {
  ...PageCourseTopicPropTypes,
  markTopicAsCompleted: PropTypes.func.isRequired,
  markCompletedOnScrollToBottom: PropTypes.bool,
  showToC: PropTypes.bool,
  // eslint-disable-next-line react/forbid-prop-types
  content: PropTypes.object
};

PageCourseTopic.defaultProps = {
  markCompletedOnScrollToBottom: false,
  content: null,
  showToC: false
};

export default connect(mapStateToProps, mapDispatchToProps)(PageCourseTopic);
